import {
  RugFormDataState,
  RugReportSectionId,
} from 'pages/reporting/reports/rug/rugReportData'
import { ComponentId, RugSectionStats } from 'pages/reporting/reports/rug/types'
import { hasSectionData } from 'pages/reporting/reports/rug/utils/calculateRUGStatsForSection'
import { createReportHeatmapSectionKey } from 'pages/reporting/reports/rug/utils/reportHeatmapStorage'
import { getSignatureForRole } from 'pages/reporting/shared/approval/approvalRules'
import { useCallback, useMemo, useState } from 'react'
import { GeckoComponent, Sectionable } from 'types'

import { AssetScreenshots } from './useAssetScreenshots'

interface SectionStatus {
  isComplete: boolean
  completedFields: number
  totalFields: number
}

/**
 * Check if a value is valid (non-null, non-empty, or valid nested structure)
 */
const isValidValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'string' && value === '') {
    return false
  }

  if (Array.isArray(value)) {
    const filtered = value.filter(
      (item) => item !== null && item !== '' && item !== undefined
    )
    return filtered.length > 0
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value)
    if (keys.length === 0) {
      return false
    }
    return keys.every((key) =>
      isValidValue((value as Record<string, unknown>)[key])
    )
  }

  return true
}

/**
 * Calculate completion status for a section based on required fields
 */
const calculateSectionCompletion = (
  requiredFields: string[],
  data: Record<string, unknown> | undefined
): SectionStatus => {
  if (!data) {
    return {
      isComplete: false,
      completedFields: 0,
      totalFields: requiredFields.length,
    }
  }
  let completedFields = 0
  const totalFields = requiredFields.length

  requiredFields.forEach((field) => {
    const value = data[field]
    if (isValidValue(value)) {
      completedFields++
    }
  })

  return {
    isComplete: completedFields > 0 && completedFields === totalFields,
    completedFields,
    totalFields,
  }
}

/**
 * Get completion status for RUG report sections
 */
export const getRugReportCompletionStatus = (
  formData: RugFormDataState,
  assetScreenshots: AssetScreenshots | undefined,
  selectedComponents: readonly GeckoComponent[] | undefined,
  sectionsByComponent: Map<ComponentId, Sectionable>,
  statsBySection: Map<ComponentId, Map<string, RugSectionStats>> | undefined
): Record<string, SectionStatus> => {
  const status: Record<string, SectionStatus> = {}

  const fallbackStatus: SectionStatus = {
    isComplete: false,
    completedFields: 0,
    totalFields: 0,
  }

  // Cover section (required)
  const coverFields = ['inspectionStartDate', 'inspectionEndDate']
  status.cover = calculateSectionCompletion(coverFields, {
    inspectionStartDate: formData.coverSectionData?.inspectionStartDate,
    inspectionEndDate: formData.coverSectionData?.inspectionEndDate,
  })

  // Summary section (required)
  const summaryFields = [
    'inspectionResolution',
    'deliverableResolution',
    'coverageScope',
    'corrosionBLUF',
  ]
  status.summary = calculateSectionCompletion(summaryFields, {
    inspectionResolution: formData.summarySectionData?.inspectionResolution,
    deliverableResolution: formData.summarySectionData?.deliverableResolution,
    coverageScope: formData.summarySectionData?.coverageScope,
    corrosionBLUF: formData.summarySectionData?.corrosionBLUF,
  })

  // Data Sheet section (required)
  const dataSheetFields = [
    'sensorSerialNumbers',
    'inspectionSurface',
    'surfaceCondition',
    'coatingPresent',
    'robots',
    'calibrationBlocks',
    'calibrationSteps',
    'payloads',
  ]
  const dataSheetData = {
    sensorSerialNumbers: formData.dataSheetSectionData?.sensorSerialNumbers,
    inspectionSurface: formData.dataSheetSectionData?.inspectionSurface,
    surfaceCondition: formData.dataSheetSectionData?.surfaceCondition,
    coatingPresent: formData.dataSheetSectionData?.coatingPresent,
    robots: formData.dataSheetSectionData?.robots,
    calibrationBlocks: formData.dataSheetSectionData?.calibrationBlocks,
    calibrationSteps: formData.dataSheetSectionData?.calibrationSteps,
    payloads: formData.dataSheetSectionData?.payloads,
  }

  status[RugReportSectionId.DATA_SHEET] = calculateSectionCompletion(
    dataSheetFields,
    dataSheetData
  )

  // Images section (optional)
  if (formData.imagesSectionData?.imageReportIds !== undefined) {
    const hasImages = formData.imagesSectionData.imageReportIds.length > 0
    status[RugReportSectionId.IMAGES] = {
      isComplete: hasImages,
      completedFields: hasImages ? 1 : 0,
      totalFields: 1,
    }
  } else {
    status[RugReportSectionId.IMAGES] = {
      ...fallbackStatus,
    }
  }

  // Data Overview section
  const componentCount = selectedComponents?.length ?? 0
  const screenshotCount = new Set(
    assetScreenshots?.fullComponentHeatmap?.map(
      (screenshot) => screenshot.componentId
    ) ?? []
  ).size
  status[RugReportSectionId.DATA_OVERVIEW] = {
    ...fallbackStatus,
    isComplete: screenshotCount === componentCount,
    completedFields: screenshotCount,
    totalFields: componentCount,
  }

  // Rug Data Images section
  const componentsWithSectionsCount = sectionsByComponent.size
  const completedComponentCount =
    selectedComponents
      ?.map((component) => {
        const sectionable = sectionsByComponent.get(component.id as ComponentId)
        if (!sectionable) return false
        const componentStats = statsBySection?.get(component.id as ComponentId)
        const sectionKeys = new Set(
          assetScreenshots?.sectionHeatmap
            ?.filter((screenshot) => screenshot.componentId === component.id)
            .map(
              (screenshot) => screenshot.sectionKey ?? screenshot.bundlePath
            ) ?? []
        )
        if (sectionable.sections.length === 0) {
          return sectionKeys.size === 1
        }
        const expectedSections = componentStats
          ? sectionable.sections.filter((section) =>
              hasSectionData(componentStats.get(section.name))
            )
          : sectionable.sections
        return (
          expectedSections.length === sectionKeys.size &&
          expectedSections.every((section) =>
            sectionKeys.has(createReportHeatmapSectionKey(section.name))
          )
        )
      })
      .reduce((acc, curr) => acc + (curr ? 1 : 0), 0) ?? 0
  status[RugReportSectionId.RUG_DATA_IMAGES] = {
    isComplete: completedComponentCount === componentsWithSectionsCount,
    completedFields: completedComponentCount,
    totalFields: componentsWithSectionsCount,
  }

  // Review section: every configured role has signed electronically
  const requiredRoles = formData.approvalWorkflow?.requiredRoles ?? []
  const signedCount = requiredRoles.filter((role) =>
    getSignatureForRole(formData.eSignatures?.records ?? [], role)
  ).length
  status[RugReportSectionId.REVIEW] = {
    isComplete:
      requiredRoles.length > 0 && signedCount === requiredRoles.length,
    completedFields: signedCount,
    totalFields: Math.max(requiredRoles.length, 1),
  }

  return status
}

interface UseReportNavigationResult {
  activePage: string | null
  navigateToPage: (page: string) => void
  returnToMenu: () => void
  completionStatus: Record<
    string,
    {
      isComplete: boolean
      completedFields: number
      totalFields: number
    }
  >
}

interface UseReportNavigationProps {
  formData: RugFormDataState
  assetScreenshots: AssetScreenshots | undefined
  selectedComponents: readonly GeckoComponent[] | undefined
  sectionsByComponent: Map<ComponentId, Sectionable>
  statsBySection: Map<ComponentId, Map<string, RugSectionStats>> | undefined
}

/**
 * Hook to manage report navigation and page completion status for RUG reports
 */
export const useReportNavigation = ({
  formData,
  assetScreenshots,
  selectedComponents,
  sectionsByComponent,
  statsBySection,
}: UseReportNavigationProps): UseReportNavigationResult => {
  const [activePage, setActivePage] = useState<string | null>(null)

  // Calculate completion status for all sections
  const completionStatus = useMemo(
    () =>
      getRugReportCompletionStatus(
        formData,
        assetScreenshots,
        selectedComponents,
        sectionsByComponent,
        statsBySection
      ),
    [
      formData,
      assetScreenshots,
      selectedComponents,
      sectionsByComponent,
      statsBySection,
    ]
  )

  /**
   * Navigate to a specific page
   */
  const navigateToPage = useCallback((page: string) => {
    setActivePage(page)
  }, [])

  /**
   * Return to the main menu
   */
  const returnToMenu = useCallback(() => {
    setActivePage(null)
  }, [])

  return {
    activePage,
    navigateToPage,
    returnToMenu,
    completionStatus,
  }
}
